import { api } from "./api";

export interface Membership {
  siteId: string;
  siteName: string;
  isHost: boolean;
  roleKey: string;
  roleName: Record<string, string>;
  isSuperadmin: boolean;
  permissions: string[];
}

export interface AccountUser {
  id: string;
  email: string;
  name: string;
  isSuperadmin: boolean;
  memberships: Membership[];
  createdAt?: string;
  /** Last sign-in (the most recent login/register audit event). */
  lastLoginAt?: string;
  /** Whether the user has a password at all (vs. OAuth only) - email-change reauth. */
  hasPassword?: boolean;
  /** Public profile photo URL (R2/WebP), or null -> initials. */
  avatarUrl?: string | null;
}

export async function getMe(): Promise<AccountUser | null> {
  try {
    return await api.get<AccountUser>("/me");
  } catch {
    return null;
  }
}

/** Login response: either the full user OR an MFA challenge (second factor needed). */
export interface MfaRequired {
  mfaRequired: true;
  pendingToken: string;
}
export type LoginResult = AccountUser | MfaRequired;

export function isMfaRequired(r: LoginResult): r is MfaRequired {
  return (r as MfaRequired).mfaRequired === true;
}

/** Funnel AI interview brand voice (path 3) - carries tone/emphasis/CTA into register.
 *  Mirrors the backend's `ai/brand-voice.ts` enums; every field is optional. */
export interface BrandVoice {
  tone?: "formal" | "friendly" | "salesy";
  emphasis?: string;
  cta?: "call" | "quote" | "book" | "contact";
}

/** Funnel selection accordion (path 3): entries UNCHECKED in the scan result - by
 *  default everything is imported, only the exceptions land here. Pages/posts by WP
 *  slug, menu by top-level label. */
export interface ImportSkip {
  pages?: string[];
  posts?: string[];
  menu?: string[];
}

/**
 * Everything the public funnel (and the account-free editor) can carry into
 * registration. An options object rather than a tail of positional parameters: this
 * context grows every time the funnel gains a path, and `register(e, n, p, s,
 * undefined, undefined, undefined, undefined, caps)` is how call sites start passing
 * the wrong argument.
 */
export interface RegisterContext {
  /** Path 2: the ready-made design the visitor picked. Exclusive with `draft`. */
  templateId?: string;
  /** Account-free editor claim: the design the visitor built BEFORE having an account
   *  (`POST /api/public/drafts`). Becomes their first site. Exclusive with `templateId`. */
  draft?: { id: string; token: string };
  /** Path 3: the old site's URL - conversion runs in the background after signup. */
  importUrl?: string;
  brandVoice?: BrandVoice;
  importSkip?: ImportSkip;
  /** Onboarding capability selection (paths 1 + 2). `[]` = saw the step and skipped it
   *  (source=skipped); `undefined` = never reached the capabilities step. */
  capabilities?: string[];
  /** Onboarding free-text brief - register classifies it in the background. */
  brief?: string;
}

export interface RegisterResult {
  user: AccountUser;
  siteId: string;
  homePageId: string | null;
  /** Present only when a `draft` was sent: how the claim went. Anything other than
   *  "applied" means the account exists but the saved design did not make it. */
  draftClaim?: "applied" | "not_found" | "expired" | "invalid_template";
}

export async function register(
  email: string,
  name: string,
  password: string,
  subdomain: string,
  ctx: RegisterContext = {},
): Promise<RegisterResult> {
  const { templateId, draft, importUrl, brandVoice, importSkip, capabilities, brief } = ctx;
  // Brand voice only with an import (path 3), and only when it has content (an empty
  // object would trip the backend's `.strict()` schema).
  const bv =
    importUrl && brandVoice && (brandVoice.tone || brandVoice.emphasis?.trim() || brandVoice.cta)
      ? {
          ...(brandVoice.tone ? { tone: brandVoice.tone } : {}),
          ...(brandVoice.emphasis?.trim() ? { emphasis: brandVoice.emphasis.trim() } : {}),
          ...(brandVoice.cta ? { cta: brandVoice.cta } : {}),
        }
      : undefined;
  // Selection exceptions only with an import, and only for non-empty lists.
  const skip =
    importUrl && importSkip
      ? {
          ...(importSkip.pages?.length ? { pages: importSkip.pages } : {}),
          ...(importSkip.posts?.length ? { posts: importSkip.posts } : {}),
          ...(importSkip.menu?.length ? { menu: importSkip.menu } : {}),
        }
      : undefined;
  const res = await api.post<{
    ok: boolean;
    siteId: string;
    homePageId: string | null;
    draftClaim?: RegisterResult["draftClaim"];
  }>("/auth/register", {
    email,
    name,
    password,
    subdomain,
    // A draft and a template id are mutually exclusive server-side; the draft wins here
    // because it is the visitor's OWN work, while the template is only a starting point.
    ...(draft ? { draft } : templateId ? { templateId } : {}),
    ...(importUrl ? { importUrl } : {}),
    ...(bv ? { brandVoice: bv } : {}),
    ...(skip && Object.keys(skip).length > 0 ? { importSkip: skip } : {}),
    // `[]` is meaningful (skipped) -> send whenever defined, not only when non-empty.
    ...(capabilities !== undefined ? { capabilities } : {}),
    // Brief mode: send when non-empty. Only together with onboarding (paths 1/2).
    ...(brief && brief.trim() ? { brief: brief.trim() } : {}),
  });
  const user = await getMe();
  if (!user) throw new Error("registration_failed");
  return {
    user,
    siteId: res.siteId,
    homePageId: res.homePageId ?? null,
    ...(res.draftClaim ? { draftClaim: res.draftClaim } : {}),
  };
}

/** Public subdomain availability check result (mirrors the backend shape). */
export type SubdomainAvailability =
  | { available: true }
  | { available: false; reason: "invalid" | "reserved" | "quarantined" | "taken" };

/** Check whether `<label>.husly.app` is free to register (live preview). On error
 *  "available: true" (fail-open) - the server enforces the final check. */
export async function checkSubdomain(label: string): Promise<SubdomainAvailability> {
  try {
    return await api.get<SubdomainAvailability>(`/public/subdomain-available?label=${encodeURIComponent(label)}`);
  } catch {
    return { available: true };
  }
}

export async function login(email: string, password: string): Promise<LoginResult> {
  return api.post<LoginResult>("/auth/login", { email, password });
}

export async function logout(): Promise<void> {
  await api.post("/auth/logout");
}

// ── Password reset ("forgot my password") ─────────────────────────────────────
/** Request a reset link. The response is always a success (anti-enumeration). */
export async function forgotPassword(email: string, locale?: string): Promise<void> {
  await api.post("/auth/password/forgot", { email, app: "account", locale });
}

/** Check that a reset token is still valid before showing the form. */
export async function resetTokenStatus(token: string): Promise<boolean> {
  try {
    const r = await api.get<{ valid: boolean }>(`/auth/password/token-status?token=${encodeURIComponent(token)}`);
    return !!r.valid;
  } catch {
    return false;
  }
}

/** Set a new password from a token. Throws ApiError (e.g. invalid_or_expired). */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await api.post("/auth/password/reset", { token, newPassword });
}

// ── "Set a password" (OAuth-only user) ────────────────────────────────────────
/** Email a signed-in password-less user a link to SET a password. The confirmation
 *  runs through the same reset page (`mode=set`). */
export async function requestSetPassword(locale?: string): Promise<{ ok: boolean; sentTo: string }> {
  return api.post("/me/password/set-request", { locale });
}

// ── Connected accounts (OAuth credentials) ────────────────────────────────────
export interface ConnectedAccount {
  id: string;
  provider: string; // e.g. "google"
  email: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export async function listConnectedAccounts(): Promise<ConnectedAccount[]> {
  const r = await api.get<{ accounts: ConnectedAccount[] }>("/me/connected-accounts");
  return r.accounts;
}

/** Unlink an external account. Throws ApiError (e.g. last_login_method). */
export async function unlinkConnectedAccount(id: string): Promise<void> {
  await api.del(`/me/connected-accounts/${id}`);
}

// ── Account deletion ──────────────────────────────────────────────────────────
/** Deletion request response. A user with a password -> immediate
 *  (`gracePeriodDays`). A password-less (Google) user -> `emailConfirmation` plus a
 *  link to the `sentTo` address. */
export interface DeleteAccountResult {
  ok: boolean;
  emailConfirmation?: boolean;
  sentTo?: string;
  gracePeriodDays?: number;
}

/** Request account deletion. The password is only for users who have one (reauth). */
export async function deleteAccount(password?: string): Promise<DeleteAccountResult> {
  return api.post<DeleteAccountResult>("/account/privacy/delete", password ? { password } : {});
}

/** Validity of a deletion-confirmation token (confirm page pre-check). */
export async function accountDeleteTokenStatus(token: string): Promise<boolean> {
  try {
    const r = await api.get<{ valid: boolean }>(`/account/privacy/delete/token-status?token=${encodeURIComponent(token)}`);
    return !!r.valid;
  } catch {
    return false;
  }
}

/** Enforce deletion from a token (second step of the password-less flow). Throws ApiError. */
export async function confirmAccountDeletion(token: string): Promise<{ ok: boolean; gracePeriodDays: number }> {
  return api.post("/account/privacy/delete/confirm", { token });
}

// ── Client-portal invite acceptance ───────────────────────────────────────────
export interface InviteStatus {
  valid: boolean;
  email?: string;
  name?: string;
  companyName?: string;
  existingAccount?: boolean;
}

/** Invite-token status (form prefill). On error/invalid, `{valid:false}`. */
export async function inviteStatus(token: string): Promise<InviteStatus> {
  try {
    return await api.get<InviteStatus>(`/portal-invite/status?token=${encodeURIComponent(token)}`);
  } catch {
    return { valid: false };
  }
}

/** Accept response: either an account was created (signed-in user) OR it already existed. */
export type AcceptInviteResult = AccountUser | { existingAccount: true; email: string };

export function isExistingAccount(r: AcceptInviteResult): r is { existingAccount: true; email: string } {
  return (r as { existingAccount?: boolean }).existingAccount === true;
}

/** Accept an invite -> create the account + sign in (the backend sets the cookie). */
export async function acceptInvite(token: string, name: string, password: string): Promise<AcceptInviteResult> {
  return api.post<AcceptInviteResult>("/portal-invite/accept", { token, name, password });
}

// ── Login email change ────────────────────────────────────────────────────────
/** Request an email change -> the confirmation link goes to the NEW address. */
export async function requestEmailChange(newEmail: string, currentPassword?: string, locale?: string): Promise<{ ok: boolean; pendingEmail: string }> {
  return api.post("/me/email/change-request", { newEmail, currentPassword, locale });
}

/** Confirmation-token status (confirm page pre-check). */
export async function emailChangeStatus(token: string): Promise<{ valid: boolean; newEmail?: string }> {
  try {
    return await api.get(`/me/email/token-status?token=${encodeURIComponent(token)}`);
  } catch {
    return { valid: false };
  }
}

/** Enforce the email change from a token. Throws ApiError (invalid_or_expired / email_taken). */
export async function confirmEmailChange(token: string): Promise<{ ok: boolean; email: string }> {
  return api.post("/me/email/confirm", { token });
}

// ── Google "Sign in with Google" ──────────────────────────────────────────────
export interface GoogleAuthConfig {
  enabled: boolean;
  clientId: string; // public client_id (for One Tap); empty when disabled
}

/** Whether Google login is enabled + the public clientId. Disabled by default (on error). */
export async function googleConfig(): Promise<GoogleAuthConfig> {
  try {
    return await api.get<GoogleAuthConfig>("/auth/google/config");
  } catch {
    return { enabled: false, clientId: "" };
  }
}

/** One Tap credential (id_token) -> sign-in (JSON). OAuth bypasses MFA. */
export async function googleOneTap(credential: string): Promise<AccountUser> {
  return api.post<AccountUser>("/auth/google/onetap", { credential, scope: "account" });
}

/** "Continue with Google" redirect-flow URL (full-page navigation, same-origin). */
export const GOOGLE_REDIRECT_URL = "/api/auth/google?scope=account";

/** Cross-app URLs are derived from the CURRENT host - build-time `VITE_*` env is NOT
 *  used, because the Vite apps are served same-origin (see deployment/husly/README.md):
 *   - dev (localhost:3314)                          -> admin :3312, site :3310
 *   - prod (account.husly.app / konto.webadmin.ee)  -> admin.<base>, <base>
 *  `<base>` = the host without its first label (account/konto), e.g. `husly.app`. */
function crossAppUrls(): { admin: string; site: string } {
  if (typeof window === "undefined") return { admin: "http://localhost:3312", site: "http://localhost:3310" };
  const { hostname, protocol } = window.location;
  if (hostname === "localhost" || hostname.endsWith(".localhost")) {
    return { admin: "http://localhost:3312", site: "http://localhost:3310" };
  }
  const base = hostname.split(".").slice(1).join(".") || hostname;
  return { admin: `${protocol}//admin.${base}`, site: `${protocol}//${base}` };
}

/** web_admin SPA address - "Open the editor/admin center ->" (SSO on the same cookie). */
export const ADMIN_URL = crossAppUrls().admin;

/** Public site (web_site) address - header nav links (Home/Pricing/Help). Prod =
 *  husly.app / webadmin.ee. The site redirects to the locale-prefixed URL itself. */
export const SITE_URL = crossAppUrls().site;

/** The current account-UI language. `setLocale` writes localStorage synchronously
 *  (see i18n/I18nProvider), so this is always the fresh choice. `?lang=` wins (the
 *  first funnel arrival, before the initializer persists it to localStorage). */
export function currentLocale(): string {
  if (typeof window === "undefined") return "et";
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  if (fromUrl === "et" || fromUrl === "en") return fromUrl;
  return window.localStorage.getItem("wa_account_locale") ?? "et";
}

/** Append `?lang=` to a cross-app link - the admin center/site are a SEPARATE origin
 *  (separate localStorage), so the language choice does not carry over otherwise. The
 *  target app honours `?lang=` and persists it under its own key. Preserves any
 *  existing query string and #fragment. */
export function withLang(url: string, locale: string = currentLocale()): string {
  const hashAt = url.indexOf("#");
  const beforeHash = hashAt === -1 ? url : url.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : url.slice(hashAt);
  const sep = beforeHash.includes("?") ? "&" : "?";
  return `${beforeHash}${sep}lang=${encodeURIComponent(locale)}${hash}`;
}
